import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { EMPTY, iif, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { decodeOrt, encodeOrt, Ort } from '../../model/ort';
import { createValidUrlParameter } from '../../shared/utils/create-valid-url-parameter.util';
import { validiereSuchUmkreis } from '../../shared/utils/validiere-such-umkreis.util';
import { RegionenUrlService } from '../facetten/instance/regionenurl.service';
import { UmkreisParameter } from '../integration/bildungsangebot.client';
import { OrtClient } from '../integration/ort.client';
import { UrlService } from '../state/url.service';

@Injectable({
  providedIn: 'root'
})
export class OrtService {
  private readonly urlService = inject(UrlService);
  private readonly ortClient = inject(OrtClient);

  public static readonly URL_PARAM_ORT = 'ort';
  public static readonly URL_PARAM_UMKREIS = 'uk';
  public static readonly URL_PARAM_POSTLEITZAHL = 'plz';
  public static readonly URL_PARAM_ORTSUNABHAENGIG = 'oua';
  public static readonly UMKREIS_VALUE_GANZER_ORT = 'ort';
  // Verwendung von OrtService.UMKREIS_VALUE_GANZER_ORT an dieser Stelle
  // führt seit Angular 15 zu einen Error beim Ausführen der Tests => An error was thrown in afterAll
  // TypeError: OrtService_1 is undefined
  public static readonly suchumkreisRange = ['ort', '5', '10', '25', '50', '100'];

  ortsunabhaengig$: Observable<boolean> = this.urlService
    .getQueryParameterValue(OrtService.URL_PARAM_ORTSUNABHAENGIG)
    .pipe(map((param) => param?.toLowerCase() === 'true'));

  //  Muster ausschließlich für die Validierung einer PLZ
  private readonly VALIDATE_REGEX_PLZ = '^\\d+$';

  umkreisParameter$: Observable<UmkreisParameter> = this.urlService
    .getMultipleQueryParameterValues([OrtService.URL_PARAM_ORT, OrtService.URL_PARAM_UMKREIS])
    .pipe(
      map((mapOfParams) => {
        const ort = mapOfParams.get(OrtService.URL_PARAM_ORT);
        const umkreis = validiereSuchUmkreis(mapOfParams.get(OrtService.URL_PARAM_UMKREIS));
        return {
          ort: ort ?? null,
          uk: umkreis
        } as UmkreisParameter;
      })
    );

  ort$: Observable<Ort | null> = this.umkreisParameter$.pipe(
    map((umkreis) => (umkreis.ort ? decodeOrt(umkreis.ort) : null))
  );

  umkreis$ = this.umkreisParameter$.pipe(
    map((umkreis) => umkreis.uk ?? null),
    map((umkreis) => umkreis ?? OrtService.UMKREIS_VALUE_GANZER_ORT)
  );

  umkreisIsGanzerOrt$: Observable<boolean> = this.umkreis$.pipe(
    map((umkreis) => umkreis === OrtService.UMKREIS_VALUE_GANZER_ORT)
  );

  postleitzahl$: Observable<string | null> = this.urlService.getQueryParameterValue(
    OrtService.URL_PARAM_POSTLEITZAHL
  );

  orteFromPostleitzahl$: Observable<string[]> = this.postleitzahl$.pipe(
    filter((plz) => plz !== null),
    switchMap((plz) =>
      iif(
        () => new RegExp(this.VALIDATE_REGEX_PLZ).test(plz!),
        of(plz),
        throwError(() => {})
      )
    ),
    switchMap((plz) => this.updateOrtByPostleitzahl(plz!)),
    // URL-Param PLZ zuruecksetzen, wenn Regex-Validierung fehlschlaegt oder keine Ort-ID gefunden wurde
    catchError(() => {
      this.updateQueryParams({
        [OrtService.URL_PARAM_POSTLEITZAHL]: null,
        [OrtService.URL_PARAM_UMKREIS]: null,
        [UrlService.SELECTED_ANGEBOT]: null,
        [UrlService.AKTUELLE_SEITE]: null
      });
      return EMPTY;
    })
  );

  /**
   * Wahr gdw. die Option "ortsunabhängig" ausgewählt werden darf.
   * Die Option ist nur aktiv, wenn bereits ein Ort ausgewählt wurde,
   * da sonst ortsunabhängig Angebot sowieso angezeigt werden.
   */
  ortsunabhaengigActive$ = this.ort$.pipe(
    // Um initial auch einen Wert zu haben → false
    startWith(null),
    map((ort) => !!ort)
  );

  async setzePlz(plz: string): Promise<void> {
    await this.updateQueryParams({
      [OrtService.URL_PARAM_POSTLEITZAHL]: plz,
      [UrlService.SELECTED_ANGEBOT]: null,
      [UrlService.AKTUELLE_SEITE]: null
    });
  }

  public async update(
    ort?: Ort | null,
    umkreis?: string,
    ortsunabhaengig?: boolean
  ): Promise<void> {
    const params: Params = {
      [UrlService.AKTUELLE_SEITE]: null,
      [UrlService.SELECTED_ANGEBOT]: null
    };

    if (ortsunabhaengig !== undefined) {
      params[OrtService.URL_PARAM_ORTSUNABHAENGIG] = this.getOrtsunabhaengigValue(ortsunabhaengig);
    }

    if (ort) {
      params[OrtService.URL_PARAM_ORT] = encodeOrt(ort);
      params[OrtService.URL_PARAM_UMKREIS] = this.getUmkreisValue(umkreis);
      params[RegionenUrlService.URL_PARAMETER] = null;
    } else {
      params[OrtService.URL_PARAM_ORT] = null;
      params[OrtService.URL_PARAM_UMKREIS] = null;
      params[OrtService.URL_PARAM_ORTSUNABHAENGIG] = null;
    }
    await this.updateQueryParams(params);
  }

  public async updateOnlyUmkreis(umkreis: string): Promise<void> {
    const params: Params = {
      [UrlService.AKTUELLE_SEITE]: null,
      [UrlService.SELECTED_ANGEBOT]: null
    };
    params[OrtService.URL_PARAM_UMKREIS] = this.getUmkreisValue(umkreis);
    params[RegionenUrlService.URL_PARAMETER] = null;
    await this.updateQueryParams(params);
  }

  public async updateOnlyOrtsunabhaengig(ortsunabhaengig: boolean): Promise<void> {
    const params: Params = {
      [UrlService.AKTUELLE_SEITE]: null,
      [UrlService.SELECTED_ANGEBOT]: null
    };
    params[OrtService.URL_PARAM_ORTSUNABHAENGIG] = this.getOrtsunabhaengigValue(ortsunabhaengig);
    await this.updateQueryParams(params);
  }

  getOrtBySuchwort(searchText: string): Observable<Array<Ort>> {
    return this.ortClient.getOrtBySuchwort(searchText);
  }

  private updateOrtByPostleitzahl(postleitzahl: string): Observable<string[]> {
    return this.ortClient.getOrtByPlz(postleitzahl).pipe(
      switchMap((orte) =>
        iif(
          () => orte.length == 1,
          of(orte),
          throwError(() => new Error())
        )
      ),
      filter((orte) => orte.length > 0),
      map((orte) => orte.map((ort) => encodeOrt(ort))),
      withLatestFrom(this.umkreis$),
      tap(([orteEncoded, umkreis]) => {
        (async () => {
          await this.updateQueryParams({
            [OrtService.URL_PARAM_POSTLEITZAHL]: null,
            [OrtService.URL_PARAM_UMKREIS]: umkreis || '25',
            [OrtService.URL_PARAM_ORT]: createValidUrlParameter(orteEncoded),
            [UrlService.SELECTED_ANGEBOT]: null,
            [UrlService.AKTUELLE_SEITE]: null
          });
        })();
      }),
      map(([orteEncoded]) => orteEncoded)
    );
  }

  private async updateQueryParams(params: Params) {
    await this.urlService.updateUrl(params);
  }

  private getUmkreisValue(umkreis?: string): string | null | undefined {
    return umkreis !== OrtService.UMKREIS_VALUE_GANZER_ORT ? umkreis : null;
  }

  private getOrtsunabhaengigValue(ortsunabhaengig?: boolean): boolean | null {
    return ortsunabhaengig === true ? ortsunabhaengig : null;
  }
}
